import Big from 'big.js';
import { DiscountDto, OrderLineOptionDto } from '../../../generated-api';
import {
  AddComponentDto,
  AddItemDto,
  AddItemOptionDto,
  CartOrderLine,
  CartOrderLineComponent,
} from '../../../types/cart';
import { collapseComponents } from '../../utils/orderLineUtils';

export const getTakeawayPrice = (orderLineDto: CartOrderLine): number => {
  return orderLineDto.takeawayPrice ?? orderLineDto.price;
};

/**
 * Recursively calculates the total option prices for nested components (eat-in).
 */
const calculateComponentOptionsPrice = (components: CartOrderLineComponent[]): Big => {
  return components.reduce((acc, component) => {
    // Add this component's option prices
    const optionPrice = component.options.reduce((optAcc, option) => {
      return optAcc.plus(new Big(option.price).times(option.quantity));
    }, new Big(0));

    // Recursively add nested component option prices
    const nestedPrice =
      component.components && component.components.length > 0
        ? calculateComponentOptionsPrice(component.components)
        : new Big(0);

    return acc.plus(optionPrice).plus(nestedPrice);
  }, new Big(0));
};

/**
 * Recursively calculates the total option prices for nested components (takeaway).
 */
const calculateComponentOptionsTakeawayPrice = (components: CartOrderLineComponent[]): Big => {
  return components.reduce((acc, component) => {
    // Add this component's option prices
    const optionTakeawayPrice = component.options.reduce((optAcc, option) => {
      return optAcc.plus(new Big(option.takeawayPrice ?? option.price).times(option.quantity));
    }, new Big(0));

    // Recursively add nested component option prices
    const nestedPrice =
      component.components && component.components.length > 0
        ? calculateComponentOptionsTakeawayPrice(component.components)
        : new Big(0);

    return acc.plus(optionTakeawayPrice).plus(nestedPrice);
  }, new Big(0));
};

export const getTotalItemTakeawayPrice = (orderLineDto: CartOrderLine): number => {
  const optionTakeawayPrice = orderLineDto.options.reduce((acc, option) => {
    return acc.plus(new Big(option.takeawayPrice ?? option.price).times(option.quantity));
  }, new Big(0));

  // Include component option prices for composite products
  const componentOptionPrice =
    orderLineDto.components && orderLineDto.components.length > 0
      ? calculateComponentOptionsTakeawayPrice(orderLineDto.components)
      : new Big(0);

  return Number(
    optionTakeawayPrice.plus(componentOptionPrice).plus(getTakeawayPrice(orderLineDto)).times(orderLineDto.quantity)
  );
};

export const getTotalItemPrice = (orderLineDto: CartOrderLine): number => {
  const optionPrice = orderLineDto.options.reduce((acc, option) => {
    return acc.plus(new Big(option.price).times(option.quantity));
  }, new Big(0));

  // Include component option prices for composite products
  const componentOptionPrice =
    orderLineDto.components && orderLineDto.components.length > 0
      ? calculateComponentOptionsPrice(orderLineDto.components)
      : new Big(0);

  return Number(optionPrice.plus(componentOptionPrice).plus(orderLineDto.price).times(orderLineDto.quantity));
};

export const calculateTotalCartPrice = (orderLines: CartOrderLine[], discount?: DiscountDto): number => {
  const total = orderLines.reduce((acc, orderLine) => {
    const total = new Big(getTotalItemPrice(orderLine));
    const isEligibleForDiscount =
      !!discount && !!orderLine.itemVariantId && discount.productIds.includes(orderLine.itemVariantId);

    const discounted = isEligibleForDiscount
      ? total.times(new Big(1).minus(new Big(discount.amount).div(100))).round(2, Big.roundHalfEven)
      : total;

    return acc.plus(discounted);
  }, new Big(0).round(2));

  return Number(total);
};

export const calculateTotalCartTakeawayPrice = (orderLines: CartOrderLine[], discount?: DiscountDto): number => {
  const totalTakeaway = orderLines.reduce((acc, orderLine) => {
    const totalTakeawayPrice = new Big(getTotalItemTakeawayPrice(orderLine));
    const isEligibleForDiscount =
      !!discount && !!orderLine.itemVariantId && discount.productIds.includes(orderLine.itemVariantId);

    const discounted = isEligibleForDiscount
      ? totalTakeawayPrice.times(new Big(1).minus(new Big(discount.amount).div(100))).round(2, Big.roundHalfEven)
      : totalTakeawayPrice;

    return acc.plus(discounted);
  }, new Big(0).round(2));

  return Number(totalTakeaway);
};

/**
 * Recursively converts AddComponentDto to CartOrderLineComponent.
 */
const convertAddComponentToOrderLineComponent = (component: AddComponentDto): CartOrderLineComponent => {
  const convertOptions = (options: AddItemOptionDto[]): OrderLineOptionDto[] => {
    return options
      .filter((opt) => opt.quantity > 0)
      .map((opt) => ({
        ...opt,
      }));
  };

  const optionPrice = Number(
    component.options.reduce((acc, option) => {
      return acc.plus(new Big(option.price).times(option.quantity));
    }, new Big(0))
  );

  const optionTakeawayPrice = Number(
    component.options.reduce((acc, option) => {
      return acc.plus(new Big(option.takeawayPrice ?? option.price).times(option.quantity));
    }, new Big(0))
  );

  // Recursively convert and collapse nested components
  const nestedComponents = component.components?.map(convertAddComponentToOrderLineComponent);
  const collapsedComponents = nestedComponents ? collapseComponents(nestedComponents) : undefined;

  const hasChildren = Boolean(collapsedComponents && collapsedComponents.length > 0);

  return {
    id: component.itemVariantId,
    name: component.name,
    quantity: 1,
    itemVariantId: component.itemVariantId,
    price: component.price,
    takeawayPrice: component.takeawayPrice,
    options: convertOptions(component.options),
    totalPrice: Number(new Big(component.price).plus(optionPrice)),
    totalTakeawayPrice: component.takeawayPrice
      ? Number(new Big(component.takeawayPrice).plus(optionTakeawayPrice))
      : undefined,
    menuSectionId: component.menuSectionId,
    sectionProductId: component.sectionProductId,
    comment: component.comment,
    commentId: component.commentId,
    courseId: component.courseId,
    unitQuantity: component.unitQuantity,
    isCompositeProduct: hasChildren,
    components: collapsedComponents,
  };
};

export const convertAddItemToOrderLine = (item: AddItemDto, quantity: number): CartOrderLine => {
  const convertOptions = (options: AddItemOptionDto[]): OrderLineOptionDto[] => {
    return options
      .filter((opt) => opt.quantity > 0)
      .map((opt) => ({
        ...opt,
      }));
  };

  const optionPrice = Number(
    item.options
      .reduce((acc, option) => {
        return acc.plus(new Big(option.price).times(option.quantity));
      }, new Big(0))
      .times(quantity)
  );

  const optionTakeawayPrice = Number(
    item.options
      .reduce((acc, option) => {
        return acc.plus(new Big(option.takeawayPrice ?? option.price).times(option.quantity));
      }, new Big(0))
      .times(quantity)
  );

  // Convert and collapse nested components
  const rawComponents = item.components?.map(convertAddComponentToOrderLineComponent);
  const components = rawComponents ? collapseComponents(rawComponents) : undefined;

  // Calculate component option prices
  const componentOptionPrice = components ? Number(calculateComponentOptionsPrice(components).times(quantity)) : 0;
  const componentOptionTakeawayPrice = components
    ? Number(calculateComponentOptionsTakeawayPrice(components).times(quantity))
    : 0;

  // Determine if this is a composite product (has nested components)
  const hasComponents = components && components.length > 0;

  return {
    id: item.itemVariantId,
    name: item.name,
    quantity: quantity,
    itemVariantId: item.itemVariantId,
    price: item.price,
    takeawayPrice: item.takeawayPrice,
    options: convertOptions(item.options),
    totalPrice: Number(new Big(item.price).times(quantity).plus(optionPrice).plus(componentOptionPrice)),
    totalTakeawayPrice: item.takeawayPrice
      ? Number(new Big(item.takeawayPrice).times(quantity).plus(optionTakeawayPrice).plus(componentOptionTakeawayPrice))
      : undefined,

    menuSectionId: item.menuSectionId,
    sectionProductId: item.sectionProductId,
    comment: item.comment,
    commentId: item.commentId,
    openPrice: item.openPrice,
    courseId: item.courseId,
    giftCardId: item.giftCardId,
    unitQuantity: item.unitQuantity,
    components: components,
    // API compatibility fields - isCompositeProduct indicates this has nested components
    isCompositeProduct: hasComponents ?? false,
    compositeProductId: undefined,
  };
};
